import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const pipelineParallelComponent: ComponentDef = {
  id: 'pipeline-parallel',
  name: 'Pipeline Parallel',
  description: 'Distribute model layers across GPUs and forward activations via P2P communication.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'pp_size', label: 'Pipeline stages', type: 'number', default: 4, min: 2, max: 16, step: 1 },
    {
      name: 'variant',
      label: 'Schedule',
      type: 'select',
      default: 'gpipe',
      options: [
        { label: 'GPipe', value: 'gpipe' },
        { label: '1F1B', value: '1f1b' },
        { label: 'Interleaved 1F1B', value: 'interleaved-1f1b' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const pp_size = Number(params.pp_size);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [d_model]);

    if (variant === 'gpipe') {
      // GPipe: all forward passes first, then all backward passes
      let current = 'x';
      // Forward phase
      for (let i = 0; i < pp_size; i++) {
        const linearId = `fwd_${i}`;
        const p2pId = `p2p_fwd_${i}`;
        g.linear(linearId, current, [d_model, d_model]);
        g.p2p(p2pId, linearId, [d_model]);
        current = p2pId;
      }
      g.cache('activation_cache', current, [d_model]);
      // Backward phase (shown in reverse)
      let bwdCurrent = 'activation_cache';
      for (let i = pp_size - 1; i >= 0; i--) {
        const bwdId = `bwd_${i}`;
        const p2pId = `p2p_bwd_${i}`;
        g.linear(bwdId, bwdCurrent, [d_model, d_model]);
        g.p2p(p2pId, bwdId, [d_model]);
        bwdCurrent = p2pId;
      }
      g.output('out', bwdCurrent);
    } else if (variant === '1f1b') {
      // 1F1B: alternating forward and backward after warmup
      let current = 'x';
      // Warmup: forward passes to fill pipeline
      for (let i = 0; i < pp_size; i++) {
        const linearId = `fwd_${i}`;
        g.linear(linearId, current, [d_model, d_model]);
        if (i < pp_size - 1) {
          const p2pId = `p2p_${i}`;
          g.p2p(p2pId, linearId, [d_model]);
          current = p2pId;
        } else {
          current = linearId;
        }
      }
      // Steady state: 1F1B alternating
      g.cache('steady_state', current, [d_model]);
      g.linear('1f1b_fwd', 'steady_state', [d_model, d_model]);
      g.linear('1f1b_bwd', '1f1b_fwd', [d_model, d_model]);
      g.p2p('1f1b_comm', '1f1b_bwd', [d_model]);
      // Cooldown: drain remaining backward passes
      g.linear('cooldown_bwd', '1f1b_comm', [d_model, d_model]);
      g.output('out', 'cooldown_bwd');
    } else {
      // Interleaved 1F1B: multiple model chunks per stage
      let current = 'x';
      const numChunks = 2;
      g.split('chunks', 'x', [d_model], `${numChunks} chunks`);
      current = 'chunks';
      // Show interleaved schedule with multiple chunks
      for (let c = 0; c < numChunks; c++) {
        for (let i = 0; i < pp_size; i++) {
          const linearId = `chunk${c}_stage${i}`;
          g.linear(linearId, current, [d_model, d_model]);
          if (i < pp_size - 1 || c < numChunks - 1) {
            const p2pId = `p2p_c${c}_s${i}`;
            g.p2p(p2pId, linearId, [d_model]);
            current = p2pId;
          } else {
            current = linearId;
          }
        }
      }
      g.output('out', current);
    }

    return g.build();
  }
};
